import { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useIntl } from 'react-intl';
import { Avatar } from '@understory-io/leaf/avatar';
import { DeferredChoiceMenu as ChoiceMenu } from '~/ui/deferred/deferred-menus';
import { cx } from '~/styled-system/css';
import { css } from '@understory-io/leaf/css';
import { normaliseLocale } from '~/lib/localization/locale';
import { buildLocaleUrl } from './language-switcher-url';
import { focusVisibleRing } from './shared';

type LanguageSwitcherProps = {
  availableLanguages: string[];
  currentLocale: string;
  defaultLanguage?: string;
};

function formatLanguageName(locale: string): string {
  let result: string;
  try {
    const intlLocale = new Intl.Locale(locale);
    const language = intlLocale.language;
    const region = intlLocale.region ?? intlLocale.maximize().region;

    const displayNames = new Intl.DisplayNames([locale], { type: 'language' });
    const languageName = displayNames.of(language) ?? language;

    const capitalized =
      languageName.charAt(0).toUpperCase() + languageName.slice(1);
    result = region ? `${capitalized}` : capitalized;
  } catch {
    result = locale;
  }

  return result;
}

export function LanguageSwitcher({
  availableLanguages,
  currentLocale,
  defaultLanguage,
}: LanguageSwitcherProps) {
  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();
  const intl = useIntl();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  if (availableLanguages.length <= 1) {
    return null;
  }

  // Dedupe by canonical locale so a storefront configured with e.g. both
  // `en` and `en-GB` doesn't list "English" twice. First occurrence wins.
  const seenCanonical = new Set<string>();
  const visibleLanguages = availableLanguages.filter((l) => {
    const canonical = normaliseLocale(l);
    if (!canonical || seenCanonical.has(canonical)) return false;
    seenCanonical.add(canonical);
    return true;
  });

  // `currentLocale` is canonical (e.g. `en-GB`) but the option ids are the raw
  // backoffice tags (`en`, `da`). Pick the raw entry that canonicalises to the
  // current locale so the menu highlights the right option.
  const canonicalCurrent = normaliseLocale(currentLocale) ?? currentLocale;
  const selectedRawKey =
    visibleLanguages.find((l) => normaliseLocale(l) === canonicalCurrent) ??
    currentLocale;

  const label = intl.formatMessage({
    defaultMessage: 'Select language',
    description: 'Aria label for the language switcher',
  });

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cx(
          focusVisibleRing,
          css({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'interactive',
            background: '[none]',
            border: 'none',
            // Match the header's IconButton padding (Leaf's icon button uses a
            // raw 11px at standard size) so the trigger lines up with the gift
            // card / menu icon buttons.
            padding: { base: 'xsmall', tablet: '[11px]' },
            // Round focus ring to match the circular flag avatar.
            borderRadius: 'full',
          })
        )}
      >
        {/* `Avatar` (FlagContent) renders its own visually-hidden locale name;
            the button's `aria-label` names the control. */}
        <Avatar flagLocale={currentLocale} size="small" />
      </button>

      <ChoiceMenu
        anchor={triggerRef}
        open={open}
        onOpenChange={setOpen}
        placement="bottom end"
        value={selectedRawKey}
        onChange={(value) => {
          if (!value) return;
          const url = buildLocaleUrl(
            pathname,
            value,
            availableLanguages,
            defaultLanguage
          );
          navigate(url + search + hash);
        }}
        items={visibleLanguages.map((locale) => ({
          value: locale,
          label: formatLanguageName(locale),
        }))}
        aria-label={label}
      />
    </>
  );
}
